import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { mockData } from '../mockData';
import HeroVisual from './HeroVisual';
import ChatWidget from './ChatWidget';
import ProductMark from './ProductMark';
import {
  Search,
  Layers,
  Rocket,
  Sparkles,
  Target,
  FileText,
  Twitter,
  Youtube,
  Mail,
  ArrowRight,
  MessageCircle,
  Menu,
  X,
  TrainFront,
  LineChart
} from 'lucide-react';
import { generateClient } from 'aws-amplify/data';
import { trackNewsletterSignup, trackSectionNavigation, track } from '../utils/analytics';

// Accent palette. Keys come from mockData (`accent` on each service and step). Written as
// complete class strings so Tailwind's content scanner can see them — building these by
// interpolation (`bg-${c}-500`) produces classes that are never generated.
const ACCENTS = {
  blue:  { tile: 'bg-blue-500',    soft: 'bg-blue-50',    text: 'text-blue-700',    ring: 'ring-blue-100' },
  pink:  { tile: 'bg-pink-500',    soft: 'bg-pink-50',    text: 'text-pink-700',    ring: 'ring-pink-100' },
  amber: { tile: 'bg-amber-500',   soft: 'bg-amber-50',   text: 'text-amber-700',   ring: 'ring-amber-100' },
  green: { tile: 'bg-brand-green', soft: 'bg-emerald-50', text: 'text-brand-greenDark', ring: 'ring-emerald-100' },
};
const accentOf = (key) => ACCENTS[key] || ACCENTS.green;

const iconMap = {
  search: Search,
  blueprint: Layers,
  rocket: Rocket,
  sparkles: Sparkles,
  target: Target,
  file: FileText,
  mail: Mail,
  twitter: Twitter,
  youtube: Youtube,
  train: TrainFront,
  chart: LineChart
};

const Homepage = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  // Newsletter
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [emailError, setEmailError] = useState('');

  const client = generateClient();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNewsletterSignup = async (e) => {
    // Validate in the page rather than leaving it to the browser's native bubble, which
    // is unstyled, inconsistent across browsers and easy for a screen reader to miss.
    // `type="email"` + `required` stay on the input, so native semantics still apply.
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) {
      setEmailError('Enter your email address so we know where to send it.');
      return;
    }
    if (!/^[^@\s]+@[^@\s.]+\.[^@\s]{2,}$/.test(trimmed)) {
      setEmailError("That doesn't look like an email address. Check it and try again.");
      return;
    }
    setEmailError('');
    setIsSubmitting(true);
    try {
      await client.models.EmailSubscription.create({
        email: email.trim(),
        status: 'PENDING',
        subscribedAt: new Date().toISOString(),
        source: 'website',
        ipAddress: '',
        userAgent: navigator.userAgent
      });
      trackNewsletterSignup();
      setSubscribed(true);
      setEmail('');
    } catch (error) {
      console.error('Error subscribing:', error);
      setEmailError("That didn't go through. Try again, or email info@automationvita.com.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      trackSectionNavigation(href.replace('#', ''));
    }
  };

  // `source` says which button opened the chat, so GA can tell which CTA converts.
  const openChat = (source) => window.dispatchEvent(new CustomEvent('openIntakeChat', { detail: { source } }));

  const trackProductClick = (name) => {
    track('product_click', { product: name });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white/90 backdrop-blur-md shadow-sm border-b border-gray-100' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-6 py-3">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center">
              <img
                src="/assets/automation-vita-header-transparent-v2.png"
                alt="Automation Vita"
                width="180"
                height="40"
                className="h-10 w-auto"
              />
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              {mockData.navigation.map((item, index) => (
                <React.Fragment key={index}>
                  {item.isButton ? (
                    <Button
                      onClick={() => scrollToSection(item.href)}
                      className="bg-brand-green hover:bg-brand-greenDark text-white px-6 py-2 rounded-full transition-all duration-300 hover:scale-105"
                    >
                      {item.label}
                    </Button>
                  ) : (
                    <button
                      onClick={() => scrollToSection(item.href)}
                      className="relative text-gray-700 hover:text-gray-900 font-medium transition-all duration-300 group"
                    >
                      {item.label}
                      <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-brand-green transition-all duration-300 group-hover:w-full"></span>
                    </button>
                  )}
                </React.Fragment>
              ))}
            </nav>

            {/* Mobile Menu Toggle */}
            <button
              className="md:hidden p-2 text-gray-700"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {isMenuOpen && (
            <nav className="md:hidden mt-3 py-3 border-t border-gray-100 bg-white rounded-b-xl">
              {mockData.navigation.map((item, index) => (
                <button
                  key={index}
                  onClick={() => { scrollToSection(item.href); setIsMenuOpen(false); }}
                  className="block w-full text-left py-3 px-2 text-gray-700 hover:text-gray-900 font-medium transition-colors duration-300"
                >
                  {item.label}
                </button>
              ))}
            </nav>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section className="min-h-[88vh] md:min-h-screen flex items-center pt-28 pb-16 px-6">
        <div className="max-w-6xl mx-auto w-full grid md:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div className="min-w-0 text-center md:text-left">
            <button
              type="button"
              onClick={() => scrollToSection(mockData.announcement.href)}
              className="inline-flex items-center gap-2 max-w-full mb-6 px-4 py-2 rounded-full bg-brand-green/10 text-brand-greenDark text-sm font-medium text-left hover:bg-brand-green/15 transition-colors"
            >
              <span>{mockData.announcement.text}</span>
              <span className="shrink-0 underline underline-offset-2">{mockData.announcement.linkText}</span>
            </button>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-display font-bold leading-[1.05] text-gray-900 tracking-tight mb-6">
              Your time back.
              <br />
              <span className="bg-gradient-to-r from-blue-600 via-brand-green to-pink-500 bg-clip-text text-transparent">
                Your life amplified.
              </span>
            </h1>
            <p className="text-lg md:text-xl text-gray-600 font-normal leading-relaxed mb-8 max-w-xl mx-auto md:mx-0">
              {mockData.hero.subheading}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start items-center">
              <Button
                onClick={() => openChat('hero')}
                className="bg-brand-green hover:bg-brand-greenDark text-white px-8 py-3 rounded-full text-lg transition-all duration-300 hover:scale-105 hover:shadow-lg !whitespace-normal sm:!whitespace-nowrap"
              >
                {mockData.hero.ctaButtons[0].text}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button
                variant="ghost"
                onClick={() => scrollToSection('#services')}
                className="text-gray-600 hover:text-gray-900 px-6 py-3 text-lg transition-all duration-300 hover:translate-y-[-2px]"
              >
                {mockData.hero.ctaButtons[1].text}
              </Button>
            </div>
          </div>
          <div className="min-w-0 md:pl-4">
            <HeroVisual />
          </div>
        </div>
      </section>

      {/* What We Automate (the offer) */}
      <section id="services" className="scroll-mt-24 py-24 px-6 bg-gradient-to-b from-blue-50/60 via-white to-pink-50/40">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-display font-bold text-gray-900 mb-5 tracking-tight">
              {mockData.services.title}
            </h2>
            <p className="text-lg text-gray-600 font-normal leading-relaxed max-w-2xl mx-auto">
              {mockData.services.subtitle}
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-6">
            {mockData.services.items.map((item, index) => {
              const IconComponent = iconMap[item.icon];
              const a = accentOf(item.accent);
              return (
                <Card key={index} className={`border-0 shadow-sm hover:shadow-lg transition-all duration-300 hover:translate-y-[-4px] bg-white ring-1 ${a.ring}`}>
                  <CardContent className="p-7">
                    <div className="flex items-start gap-4">
                      <div className={`w-11 h-11 shrink-0 ${a.tile} rounded-xl flex items-center justify-center`}>
                        <IconComponent className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className={`text-lg font-display font-semibold ${a.text} mb-1`}>{item.outcome}</p>
                        <h3 className="text-sm font-medium text-gray-500 mb-2">{item.name}</h3>
                        <p className="text-gray-600 font-normal leading-relaxed">{item.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="max-w-2xl mx-auto mt-10 rounded-2xl bg-amber-50 ring-1 ring-amber-200 px-6 py-5 text-center">
            <p className="text-amber-950 font-medium leading-relaxed">{mockData.services.guarantee}</p>
          </div>

          <div className="text-center mt-8">
            <p className="text-sm text-gray-600 mb-5">{mockData.services.founderNote}</p>
            <Button
              onClick={() => scrollToSection('#contact')}
              className="bg-brand-green hover:bg-brand-greenDark text-white px-8 py-3 rounded-full text-lg transition-all duration-300 hover:scale-105 !whitespace-normal sm:!whitespace-nowrap"
            >
              Tell us what&rsquo;s eating your time
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="scroll-mt-24 py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-display font-bold text-gray-900 mb-5 tracking-tight">
              {mockData.howItWorks.title}
            </h2>
            <p className="text-lg text-gray-600 font-normal leading-relaxed max-w-2xl mx-auto">
              {mockData.howItWorks.subtitle}
            </p>
          </div>

          <div className="relative">
            {/* connector line behind the step circles (desktop) */}
            <div className="hidden md:block absolute top-10 left-[12.5%] right-[12.5%] h-0.5 bg-gradient-to-r from-blue-400 via-pink-400 to-brand-green opacity-50" aria-hidden="true"></div>
            <div className="relative grid md:grid-cols-4 gap-8">
              {mockData.howItWorks.steps.map((step, index) => {
                const IconComponent = iconMap[step.icon];
                const a = accentOf(step.accent);
                return (
                  <div key={index} className="text-center group">
                    <div className="relative mb-8">
                      <div className={`w-20 h-20 mx-auto ${a.soft} rounded-full shadow-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                        <IconComponent className={`w-10 h-10 ${a.text}`} />
                      </div>
                      <div className={`absolute -top-2 -right-2 w-8 h-8 ${a.tile} rounded-full flex items-center justify-center`}>
                        <span className="text-white text-sm font-medium">{step.number}</span>
                      </div>
                    </div>
                    <h3 className="text-xl font-display font-semibold text-gray-900 mb-3">{step.title}</h3>
                    <p className="text-gray-600 font-normal leading-relaxed">{step.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Sample Blueprint (proof) */}
      <section id="sample" className="scroll-mt-24 py-24 px-6 bg-gradient-to-b from-emerald-50/70 to-white">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-4xl md:text-5xl font-display font-bold text-gray-900 mb-5 tracking-tight">
              {mockData.proof.title}
            </h2>
            <p className="text-lg text-gray-600 font-normal leading-relaxed max-w-2xl mx-auto">
              {mockData.proof.subtitle}
            </p>
          </div>
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="bg-gradient-to-r from-brand-green to-blue-600 text-white px-6 py-3 font-display font-semibold flex items-center gap-2">
              <FileText className="w-5 h-5" />
              {mockData.proof.label}
            </div>
            <div className="p-6 md:p-8 space-y-5">
              {mockData.proof.sections.map((s, index) => (
                <div key={index}>
                  <p className="text-sm font-semibold text-brand-greenDark mb-1">{s.h}</p>
                  <p className="text-gray-700 font-normal leading-relaxed">{s.b}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="text-center mt-8">
            <Button
              onClick={() => openChat('proof')}
              className="bg-brand-green hover:bg-brand-greenDark text-white px-8 py-3 rounded-full text-lg transition-all duration-300 hover:scale-105 !whitespace-normal sm:!whitespace-nowrap"
            >
              {mockData.proof.cta}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Products We're Building */}
      <section id="products" className="scroll-mt-24 py-24 px-6 bg-gradient-to-b from-white via-amber-50/30 to-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-display font-bold text-gray-900 mb-5 tracking-tight">
              {mockData.products.title}
            </h2>
            <p className="text-lg text-gray-600 font-normal leading-relaxed max-w-2xl mx-auto">
              {mockData.products.subtitle}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {mockData.products.products.map((product, index) => (
              <Card
                key={index}
                className="border-0 shadow-sm hover:shadow-lg transition-all duration-300 hover:translate-y-[-5px] bg-white overflow-hidden"
              >
                {/* brand accent — each product keeps its own identity */}
                <div className="h-1.5 w-full" style={{ backgroundColor: product.accent }} aria-hidden="true" />
                <CardContent className="p-8 flex flex-col h-full">
                  <div className="flex items-center justify-between gap-4 mb-5">
                    <div className="flex items-center gap-3 min-w-0">
                      <ProductMark mark={product.mark} size={44} title={product.name} />
                      <span className="font-display font-semibold text-lg text-gray-900 truncate">
                        {product.wordmark[0]}
                        <span style={{ color: product.accent }}>{product.wordmark[1]}</span>
                      </span>
                    </div>
                    <span
                      className="inline-block shrink-0 px-3 py-1 rounded-full text-xs font-medium"
                      style={{ backgroundColor: `${product.accent}1A`, color: product.action }}
                    >
                      {product.status}
                    </span>
                  </div>
                  <p className="text-xl font-display font-semibold text-gray-900 mb-3">{product.outcome}</p>
                  <p className="text-gray-600 font-normal leading-relaxed mb-4">{product.description}</p>
                  <p className="text-gray-800 font-medium mb-1">{product.price}</p>
                  <p className="text-sm text-gray-500 mb-6">{product.note}</p>
                  <Button
                    asChild
                    className="mt-auto w-full text-white transition-all duration-300 hover:scale-105"
                    style={{ backgroundColor: product.action }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = product.actionHover; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = product.action; }}
                  >
                    <a
                      href={product.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => trackProductClick(product.name)}
                    >
                      {product.ctaText}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </a>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          <p className="text-center text-sm text-gray-600 mt-8">{mockData.products.founderNote}</p>
        </div>
      </section>

      {/* Intake / Contact Section */}
      <section id="contact" className="scroll-mt-24 py-24 px-6">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-4xl md:text-5xl font-display font-bold text-gray-900 mb-5 tracking-tight">
              {mockData.intake.title}
            </h2>
            <p className="text-lg text-gray-600 font-normal leading-relaxed">
              {mockData.intake.subtitle}
            </p>
          </div>

          <div className="text-center bg-gray-50 rounded-2xl p-10">
            <div className="w-14 h-14 mx-auto mb-4 bg-brand-green/10 rounded-full flex items-center justify-center">
              <MessageCircle className="w-7 h-7 text-brand-green" />
            </div>
            <p className="text-gray-600 font-normal leading-relaxed mb-6 max-w-md mx-auto">
              Chat with our AI concierge now — describe the task draining your week and it&rsquo;ll
              draft you a one-page automation blueprint on the spot. Matthew reads it himself and
              sends the worked-up version within two business days.
            </p>
            <Button
              onClick={() => openChat('contact')}
              className="bg-brand-green hover:bg-brand-greenDark text-white px-8 py-3 rounded-full text-lg transition-all duration-300 hover:scale-105 !whitespace-normal sm:!whitespace-nowrap"
            >
              Start the chat
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>

          {/* Newsletter (secondary) */}
          <div className="mt-14 pt-10 border-t border-gray-100 text-center">
            <h3 className="text-xl font-display font-semibold text-gray-900 mb-2">{mockData.newsletter.title}</h3>
            <p className="text-gray-600 font-normal leading-relaxed mb-6 max-w-md mx-auto">{mockData.newsletter.description}</p>
            {subscribed ? (
              <p className="text-brand-greenDark font-medium">{mockData.newsletter.success}</p>
            ) : (
              <form onSubmit={handleNewsletterSignup} noValidate className="max-w-md mx-auto">
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <label htmlFor="newsletter-email" className="sr-only">Email address</label>
                <Input
                  id="newsletter-email"
                  name="newsletter-email"
                  type="email"
                  autoComplete="email"
                  placeholder={mockData.newsletter.placeholder}
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); if (emailError) setEmailError(''); }}
                  aria-invalid={emailError ? 'true' : undefined}
                  aria-describedby={emailError ? 'newsletter-email-error' : undefined}
                  className={`flex-1 px-5 py-3 rounded-full transition-colors ${
                    emailError
                      ? 'border-red-500 focus:border-red-500'
                      : 'border-gray-200 focus:border-brand-green'
                  }`}
                  required
                />
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-gray-900 hover:bg-gray-800 text-white px-6 py-3 rounded-full transition-all duration-300 hover:scale-105 disabled:opacity-60"
                >
                  {isSubmitting ? '…' : mockData.newsletter.buttonText}
                </Button>
                </div>
                {emailError && (
                  <p id="newsletter-email-error" role="alert" className="mt-3 text-sm text-red-600">
                    {emailError}
                  </p>
                )}
              </form>
            )}
          </div>

          {/* Email + Social */}
          <div className="mt-12 flex flex-col items-center gap-5">
            <a
              href={`mailto:${mockData.contact.email}`}
              onClick={() => track('contact_email_click')}
              className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <Mail className="w-5 h-5" />
              {mockData.contact.email}
            </a>
            <div className="flex gap-4">
              {mockData.contact.social.map((social, index) => {
                const IconComponent = iconMap[social.icon];
                return (
                  <a
                    key={index}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={social.platform}
                    className="w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110"
                  >
                    <IconComponent className="w-5 h-5 text-gray-600" />
                  </a>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 bg-gray-900 text-white">
        <div className="max-w-6xl mx-auto text-center">
          <div className="flex items-center justify-center mb-6">
            <img
              src="/assets/automation-vita-header-transparent-white-text.png"
              alt="Automation Vita"
              width="252"
              height="56"
              className="h-14 w-auto"
            />
          </div>
          <p className="text-gray-400 font-normal">
            © 2026 Automation Vita. Automation that gives you your time back.
          </p>
          <p className="mt-3 flex flex-wrap justify-center gap-x-5 gap-y-2">
            {mockData.footerLinks.map((l, index) => {
              const external = l.href.startsWith('http');
              return (
                <a
                  key={index}
                  href={l.href}
                  {...(external ? { target: '_blank', rel: 'noopener noreferrer', onClick: () => trackProductClick(l.label) } : {})}
                  className="text-sm text-gray-500 hover:text-gray-300 underline underline-offset-2 transition-colors"
                >
                  {l.label}
                </a>
              );
            })}
          </p>
        </div>
      </footer>

      <ChatWidget />
    </div>
  );
};

export default Homepage;
